import React from "react";
import { COLORS, FONT_BODY } from "../theme.js";
import { Field, Section, Chip } from "./ui.jsx";
import { inputStyle, selectStyle, secondaryBtnStyle } from "./styles.js";
import { categoryMeta, SNEAKER_SIZES, AUTH_SOURCES } from "../domain/categories.js";
import { STATUS_OPTIONS } from "../domain/status.js";
import { toggleMaterial } from "../domain/itemForm.js";

/* Presentational field groups shared by the Add wizard (one per step) and the
   Edit screen (all at once). They hold no state: the parent owns the form and
   passes `set(key, value)`. */

export function IdentityFields({ form, set, onScan, scanNote, catalogueSearch }) {
  const meta = categoryMeta(form.category);
  const isSneakers = form.category === "Sneakers";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {onScan && (
        <>
          <button type="button" onClick={onScan} style={{ ...secondaryBtnStyle, textAlign: "center" }}>
            📷  Scan barcode to autofill
          </button>
          {scanNote && <span style={{ fontSize: 11.5, color: COLORS.creamDim, fontFamily: FONT_BODY }}>{scanNote}</span>}
        </>
      )}

      {/* The Add wizard passes catalogue search; the Edit screen does not — an
          item already in someone's passport is theirs, not a catalogue row. */}
      {catalogueSearch}

      <Field label="Brand">
        <input value={form.brand} onChange={(e) => set("brand", e.target.value)} placeholder={isSneakers ? "e.g. Jordan" : "e.g. Louis Vuitton"} style={inputStyle} />
      </Field>

      <Field label="Model">
        <input value={form.model} onChange={(e) => set("model", e.target.value)} placeholder={isSneakers ? "e.g. Air Jordan 1" : "e.g. Neverfull MM"} style={inputStyle} />
      </Field>

      <Field label={meta.identityFields.colorway}>
        <input value={form.colorway} onChange={(e) => set("colorway", e.target.value)} placeholder={isSneakers ? "e.g. Chicago" : "e.g. Monogram Eclipse"} style={inputStyle} />
      </Field>

      <div style={{ display: "flex", gap: 12 }}>
        <Field label={meta.identityFields.sku} style={{ flex: 1, minWidth: 0 }}>
          <input value={form.sku} onChange={(e) => set("sku", e.target.value)} placeholder={isSneakers ? "555088-101" : "SD1160"} style={inputStyle} />
        </Field>
        {meta.hasSizes && (
          <Field label="Size" style={{ flex: 1, minWidth: 0 }}>
            <select value={form.size} onChange={(e) => set("size", e.target.value)} style={selectStyle}>
              {SNEAKER_SIZES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>
        )}
      </div>

      <Field label="Release date">
        <input type="date" value={form.releaseDate} onChange={(e) => set("releaseDate", e.target.value)} style={inputStyle} />
      </Field>
    </div>
  );
}

export function PurchaseFields({ form, set }) {
  const isSneakers = form.category === "Sneakers";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Field label="Purchase date">
        <input type="date" value={form.purchaseDate} onChange={(e) => set("purchaseDate", e.target.value)} style={inputStyle} />
      </Field>

      <div style={{ display: "flex", gap: 12 }}>
        <Field label="Purchase price" style={{ flex: 1, minWidth: 0 }}>
          <input value={form.retail} onChange={(e) => set("retail", e.target.value)} placeholder="$" inputMode="numeric" style={inputStyle} />
        </Field>
        <Field label="Est. market value" style={{ flex: 1, minWidth: 0 }}>
          <input value={form.value} onChange={(e) => set("value", e.target.value)} placeholder="$" inputMode="numeric" style={inputStyle} />
        </Field>
      </div>

      <Field label="Purchased from">
        <input value={form.purchasedFrom} onChange={(e) => set("purchasedFrom", e.target.value)} placeholder={isSneakers ? "e.g. Foot Locker" : "e.g. Saks Fifth Avenue"} style={inputStyle} />
      </Field>

      <Field label="Authentication">
        <select value={form.authentication} onChange={(e) => set("authentication", e.target.value)} style={selectStyle}>
          {AUTH_SOURCES.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </Field>

      <Field label="Authentication date">
        <input type="date" value={form.authDate} onChange={(e) => set("authDate", e.target.value)} style={inputStyle} />
      </Field>
    </div>
  );
}

/** Material groups + wear-habit status — the inputs the preservation profile
 *  is generated from. */
export function DnaFields({ form, setForm }) {
  const meta = categoryMeta(form.category);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {meta.groups.map((g) => (
        <Section key={g.key} title={g.title}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {g.options.map((m) => (
              <Chip
                key={m}
                label={m}
                active={(form.materials[g.key] || []).includes(m)}
                onClick={() => setForm(toggleMaterial(form, g.key, m))}
              />
            ))}
          </div>
        </Section>
      ))}

      <Section title="Wear-habit status">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {STATUS_OPTIONS.map((s) => (
            <Chip key={s.key} label={s.label} active={form.status === s.key} onClick={() => setForm({ ...form, status: s.key })} />
          ))}
        </div>
      </Section>
    </div>
  );
}
